// PST-570 — region(국내/해외) 확정 공통 로직.
// 각 앱/화면은 "신호 소스"만 우선순위대로 나열하고, 첫 확정값(없으면 fallback)을 취한다.
// SSR·CSR 어디서든 쓰는 순수 함수 — DOM/쿠키 접근 없음(값만 받아 판별).
//
// 사용 예 (프로모션 상세 SSR):
//   const region = resolveRegion([
//     regionFromQueryParam(query.isDomestic),        // URL ?isDomestic=
//     regionFromIsDomestic(promotion.isDomestic),    // 콘텐츠(프로모션) 신호
//     regionFromCookie(req.cookies['mrt_region']),   // 연속성 쿠키
//   ]);                                              // 없으면 'overseas'

import { HeaderRegion } from './types';

/** isDomestic 불리언(콘텐츠/주입 신호) → region. null/undefined = 신호 없음. */
export function regionFromIsDomestic(
  isDomestic: boolean | null | undefined,
): HeaderRegion | undefined {
  if (isDomestic == null) return undefined;
  return isDomestic ? 'domestic' : 'overseas';
}

/**
 * URL query 의 isDomestic('true'|'false'|그 외) → region. 배열/미지정/null 은 신호 없음.
 * (Next router.query 의 `string | string[] | undefined`, URLSearchParams.get 의 `string | null` 모두 수용.)
 */
export function regionFromQueryParam(
  value: string | string[] | null | undefined,
): HeaderRegion | undefined {
  if (value === 'true') return 'domestic';
  if (value === 'false') return 'overseas';
  return undefined;
}

/** 헤더 region 연속성 쿠키 이름(단일 소스). 소비처는 이 이름을 하드코딩하지 않는다. */
export const REGION_COOKIE_NAME = 'mrt_region';

/** 연속성 쿠키 값('domestic'|'overseas') → region. */
export function regionFromCookie(value: string | undefined): HeaderRegion | undefined {
  if (value === 'domestic') return 'domestic';
  if (value === 'overseas') return 'overseas';
  return undefined;
}

/**
 * 쿠키 jar(SSR 의 req.cookies 등) → region. 쿠키 이름(mrt_region)은 여기서만 안다.
 * 소비처는 이름 없이 jar 만 넘긴다: regionFromCookies(req.cookies).
 * (client 는 ambient 읽기가 가능한 readRegionCookie() 를 사용 — jar 전달 불필요.)
 */
export function regionFromCookies(
  cookies: Partial<Record<string, string>> | undefined,
): HeaderRegion | undefined {
  return regionFromCookie(cookies?.[REGION_COOKIE_NAME]);
}

/**
 * 우선순위 후보를 순서대로 받아 첫 확정 region 을 반환, 없으면 fallback(기본 overseas).
 * 후보는 각 헬퍼(regionFrom*)로 만들어 넣는다 — undefined 는 "신호 없음"으로 건너뛴다.
 */
export function resolveRegion(
  candidates: Array<HeaderRegion | undefined>,
  fallback: HeaderRegion = 'overseas',
): HeaderRegion {
  return candidates.find((region): region is HeaderRegion => region != null) ?? fallback;
}
